import {
  BreadcrumbJsonLd,
  NextSeo,
  NextSeoProps,
  OrganizationJsonLd,
  SiteLinksSearchBoxJsonLd,
} from 'next-seo'
import { useRouter } from 'next/router'
import { ReactFC } from 'common/types/ReactFC'
import { seoStrings } from 'common/strings/seo-strings'
import { ItemListElements } from 'next-seo/lib/types'
import Head from 'next/head'
import { Constants } from 'common/utils'
import { getTenantId, Tenant } from 'common/tenant'

const CURRENT_URL = process.env.SITE_URL

const getCanonical = (path: string) => {
  const fullURL = new URL(path, CURRENT_URL)
  return `${fullURL.origin}${fullURL.pathname}`
}

export type SeoContainerProps = {
  seoProps?: NextSeoProps
  noIndex?: boolean
  noFollow?: boolean
  canonical?: string
  breadcrumb: ItemListElements[]
  hreflang?: 'en-au' | 'en-gb'
  alternate?: { lang: 'en-gb' | 'en-au' | 'x-default'; href: string }[]
  tenant?: Tenant
}
export const SeoContainer: ReactFC<SeoContainerProps> = ({
  alternate,
  tenant: tenantProp,
  breadcrumb,
  canonical,
  children,
  hreflang = 'en-gb',
  noFollow: _noFollow,
  noIndex: _noIndex,
  seoProps,
}) => {
  const { asPath, pathname } = useRouter()
  // Pages under /au are AU unless a tenant is passed explicitly
  const tenant = tenantProp ?? getTenantId(pathname)
  const au = tenant === Tenant.AU
  const isDev = !process.env.prod && process.env.NODE_ENV !== 'development'
  const noIndex = _noIndex || isDev
  const noFollow = (_noFollow ?? _noIndex) || isDev
  return (
    <>
      <Head>
        {alternate?.map(({ href, lang }) => {
          let link = getCanonical(href)
          if (!link.endsWith('/')) {
            link += '/'
          }
          return <link key={lang} rel='alternate' hrefLang={lang} href={link} />
        })}
        <link rel='canonical' href={canonical || getCanonical(asPath)} />
      </Head>
      <BreadcrumbJsonLd itemListElements={breadcrumb} />
      <SiteLinksSearchBoxJsonLd
        name={seoStrings.appSiteName}
        url={getCanonical(`/`)}
        potentialActions={[
          {
            queryInput: 'search_term_string',
            target: `${process.env.SITE_URL}/blog/search?q`,
          },
        ]}
      />
      <NextSeo
        {...seoProps}
        noindex={!!noIndex}
        nofollow={!!noFollow}
        openGraph={{
          description: seoProps?.description,
          images: [
            {
              alt: seoStrings.appOGLogoAlt,
              height: 226,
              url: seoStrings.appOGLogo,
              width: 400,
            },
          ],
          locale: au ? 'en_AU' : 'en_GB',
          site_name: seoStrings.appSiteName,
          title: seoProps?.title,
          type: 'website',
          ...(seoProps?.openGraph || {}),
        }}
        twitter={{
          cardType: 'summary',
          handle: Constants.companyInformation.twitter_handle,
          site: Constants.companyInformation.twitter_handle,
          ...(seoProps?.twitter || {}),
        }}
      />
      <OrganizationJsonLd
        name={seoStrings.organisationName}
        url={au ? 'https://www.hoxtonmix.com/au/' : 'https://www.hoxtonmix.com/'}
        telephone={au ? '+61 2 8530 0437' : '+44 20 3475 3374'}
        address={
          au
            ? {
                addressCountry: 'AU',
                addressLocality: 'Sydney',
                addressRegion: 'NSW',
                postalCode: '2000',
                streetAddress: Constants.companyInformation.address2Au,
              }
            : {
                addressCountry: 'UK',
                addressLocality: 'London',
                addressRegion: 'Greater London',
                postalCode: Constants.companyInformation.postal_code,
                streetAddress: Constants.companyInformation.address2,
              }
        }
        logo={'https://www.hoxtonmix.com/organisation.png'}
        sameAs={[
          'https://twitter.com/hoxtonmix',
          'https://www.facebook.com/hoxtonmix/',
          'https://www.linkedin.com/company/hoxton-mix/',
          'https://www.youtube.com/c/Hoxtonmix',
          'https://www.instagram.com/hoxtonmix/',
        ]}
      />
      {children}
    </>
  )
}

SeoContainer.displayName = 'SeoContainer'
export default SeoContainer
